import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Wifi, 
  Clock, 
  DollarSign,
  AlertTriangle,
  Database
} from 'lucide-react';
import { useDiagnostics } from './DiagnosticsContext';

const DiagnosticsStats: React.FC = () => {
  const { stats } = useDiagnostics();

  const primaryStatCards = [
    {
      title: 'IoT Инфраструктура',
      value: `${stats.iotStats.activePercent.toFixed(1)}%`,
      icon: Wifi,
      details: [
        { label: 'С IoT модулем', value: stats.iotStats.withIoT, color: 'blue' },
        { label: 'В сети', value: stats.iotStats.onlineDevices, color: 'green' },
        { label: 'Офлайн', value: stats.iotStats.offlineDevices, color: 'red' },
        { label: 'Покрытие IoT', value: `${stats.iotStats.coveragePercent.toFixed(1)}%`, color: 'blue' }
      ]
    },
    {
      title: 'Аренды',
      value: stats.totalRentals,
      icon: Clock,
      details: [
        { label: 'Активные', value: stats.activeRentals, color: 'blue' },
        { label: 'Подозрительные', value: stats.suspiciousRentals, color: 'yellow' },
        { label: 'Выручка', value: `${stats.totalRevenue.toLocaleString()} ₽`, color: 'green' },
        { label: 'Потенциальные потери', value: `${stats.suspiciousRentalStats.potentialLoss.toLocaleString()} ₽`, color: 'red' }
      ]
    },
    {
      title: 'Финансовая статистика',
      value: `${stats.financialStats.successRate.toFixed(1)}%`,
      icon: DollarSign,
      details: [
        { label: 'Всего платежей', value: stats.financialStats.totalPayments, color: 'blue' },
        { label: 'Успешные', value: stats.financialStats.successfulPayments, color: 'green' },
        { label: 'Неудачные', value: stats.financialStats.failedPayments, color: 'red' },
        { label: 'Покрытие чеками', value: `${stats.financialStats.receiptCoverage.toFixed(1)}%`, color: 'blue' }
      ]
    }
  ];

  const detailedStatCards = [
    {
      title: 'Подозрительные аренды',
      value: stats.suspiciousRentalStats.totalSuspicious,
      icon: AlertTriangle,
      details: [
        { label: 'Критические недоплаты', value: stats.suspiciousRentalStats.criticalUnderpayments, color: 'red' },
        { label: 'Временные нарушения', value: stats.suspiciousRentalStats.temporalViolations, color: 'yellow' },
        { label: 'Точность обнаружения', value: `${stats.suspiciousRentalStats.detectionAccuracy.toFixed(1)}%`, color: 'green' },
        { label: 'Покрытие диагностики', value: `${stats.suspiciousRentalStats.coverageRate.toFixed(1)}%`, color: 'blue' }
      ]
    }
  ];

  const renderStatCard = (card: any, index: number) => (
    <Card key={index} className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
        <card.icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold mb-3">{card.value}</div>
        <div className="space-y-2">
          {card.details.map((detail: any, idx: number) => (
            <div key={idx} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{detail.label}</span>
              <Badge 
                variant={
                  detail.color === 'green' ? 'default' : 
                  detail.color === 'red' ? 'destructive' : 
                  detail.color === 'yellow' ? 'secondary' : 
                  detail.color === 'orange' ? 'secondary' :
                  'outline'
                }
                className="text-xs px-2 py-1"
              >
                {detail.value}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Основные метрики */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Database className="h-5 w-5" />
          Основные метрики системы
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {primaryStatCards.map((card, index) => renderStatCard(card, index))}
        </div>
      </div>

      {/* Детальная диагностика */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Детальная диагностика
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {detailedStatCards.map((card, index) => renderStatCard(card, index))}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticsStats;
